import { css } from "styled-system/css";
import { getSeriesColor } from "./theme";

interface DataPoint {
  name: string;
  value: number;
  color?: string;
  percentage?: number;
}

interface CategoryBarsProps {
  data: DataPoint[];
  formatValue?: (value: number) => string;
}

// Horizontal bar breakdown — the design system's replacement for pie/donut
// charts ("use instead of pie"). Flat fills, no gradients, no shadows.
export function CategoryBars({
  data,
  formatValue = (v) => v.toLocaleString(),
}: CategoryBarsProps) {
  const max = Math.max(...data.map((d) => d.value), 1);
  const total = data.reduce((sum, d) => sum + d.value, 0);

  return (
    <div className={styles.list}>
      {data.map((entry, index) => {
        const pct =
          entry.percentage ??
          (total > 0 ? Math.round((entry.value / total) * 100) : 0);
        return (
          <div key={entry.name} className={styles.row}>
            <span className={styles.label}>{entry.name}</span>
            <div className={styles.track}>
              <div
                className={styles.bar}
                style={{
                  width: `${(entry.value / max) * 100}%`,
                  backgroundColor: entry.color || getSeriesColor(index),
                }}
              />
            </div>
            <span className={styles.value}>{formatValue(entry.value)}</span>
            <span className={styles.pct}>{pct}%</span>
          </div>
        );
      })}
    </div>
  );
}

const styles = {
  list: css({
    display: "flex",
    flexDirection: "column",
    gap: 2.5,
    width: "100%",
  }),
  row: css({
    display: "grid",
    gridTemplateColumns: "110px 1fr 48px 40px",
    alignItems: "center",
    gap: 3,
  }),
  label: css({
    fontSize: "sm",
    fontWeight: "medium",
    color: "fg.default",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
  track: css({
    height: "16px",
    bg: "bg.muted",
    borderRadius: "sm",
    overflow: "hidden",
  }),
  bar: css({
    height: "100%",
    borderRadius: "sm",
    transition: "width 300ms cubic-bezier(0.2, 0, 0, 1)",
  }),
  value: css({
    fontSize: "sm",
    fontWeight: "semibold",
    color: "fg.default",
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  }),
  pct: css({
    fontSize: "xs",
    color: "fg.muted",
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  }),
};
